import * as Yup from "yup";
import moment from "moment";
import { REGULAR_EXPRESSION } from "../../constants/regular-expressions";
import { COUNTRY_OPTIONS, INTEGRATION_TYPE } from "../../constants/common";
/*istanbul ignore next */
const phoneWithExtensionSchema = Yup.string()
  .test("phone-extension", "Incorrect format", (value) => {
    if (!value) return true;
    const [phonePart, extensionPart] = value.split(",");
    return (
      REGULAR_EXPRESSION.PHONE_NUMBER.test(phonePart) &&
      (!extensionPart || /^\d{2,6}$/.test(extensionPart))
    );
  })
  .required("");
/*istanbul ignore next */
const secondaryPhoneWithExtensionSchema = Yup.string()
  .test("combined-validation", "Invalid number", (value, context) => {
    if (!value) return true;
    const [phonePart, extensionPart] = value.split(",");
    const primaryPhoneNumber = context.parent.phoneNumber;
    if (phonePart === primaryPhoneNumber) return false;
    if (!REGULAR_EXPRESSION.PHONE_NUMBER.test(phonePart)) return false;
    if (extensionPart && !/^\d{2,6}$/.test(extensionPart)) return false;

    return true;
  })
  .nullable(true);

/* =====================================================
   COMMON SCHEMA
===================================================== */
/*istanbul ignore next */
const CommonSchema = {
  mobile: Yup.string()
    .matches(REGULAR_EXPRESSION.PHONE_NUMBER, "Incorrect format")
    .nullable(true),
  fax: Yup.string()
    .matches(REGULAR_EXPRESSION.PHONE_NUMBER, "Incorrect format")
    .nullable(true),
  mobile1: Yup.string()
    .matches(REGULAR_EXPRESSION.PHONE_NUMBER, "Incorrect format")
    .nullable(true),
  fax1: Yup.string()
    .matches(REGULAR_EXPRESSION.PHONE_NUMBER, "Incorrect format")
    .nullable(true),
  firstName: Yup.string()
    .min(2, "Too Short!")
    .max(25, "Too Long!")
    .matches(
      REGULAR_EXPRESSION.NAME,
      "Only Alphabets, Hyphen(-) & Apostrophe(') allowed",
    )
    .required(""),
  lastName: Yup.string()
    .min(2, "Too Short!")
    .max(25, "Too Long!")
    .matches(
      REGULAR_EXPRESSION.NAME,
      "Only Alphabets, Hyphen(-) & Apostrophe(') allowed",
    )
    .required(""),
  distributorType: Yup.array().min(1, "Select Distributor").required(""),
  primaryEmail: Yup.string()
    .max(50, "Cannot be greater than 50 characters")
    .email("Invalid email")
    .required(""),
  secondaryEmail: Yup.string().email("Invalid email").nullable(true),
  phoneNumber: phoneWithExtensionSchema,
  secondaryPhoneNumber: secondaryPhoneWithExtensionSchema,
  billingPhoneNumber: Yup.string()
    .matches(REGULAR_EXPRESSION.PHONE_NUMBER, "Incorrect format")
    .nullable(true),

  zip: Yup.string().when("countryId", (countryId, schema) =>
    countryId === COUNTRY_OPTIONS.CANADA
      ? schema
          .matches(
            REGULAR_EXPRESSION.ZIP_CODE_CANADA,
            "Invalid Postal Code. Format is ANA NAN, where 'A' is an uppercase alphabet and 'N' is a digit.",
          )
          .nullable(true)
      : schema
          .matches(
            REGULAR_EXPRESSION.ZIP_CODE_USA,
            "Invalid Zip Code. Please enter a 5-digit Zip Code or a 9-digit ZIP+4 code in the format 12345 or 12345-6789.",
          )
          .nullable(true),
  ),
  websiteUrl: Yup.string()
    .matches(REGULAR_EXPRESSION.URL, "Must be a valid url")
    .nullable(true),
  numberOfOwners: Yup.string()
    .matches(REGULAR_EXPRESSION.NUMBER, "Must be only digits")
    .nullable(true),
  businessYear: Yup.string()
    .matches(REGULAR_EXPRESSION.NUMBER, "Must be only digits")
    .nullable(true),
  producers: Yup.string()
    .matches(REGULAR_EXPRESSION.NUMBER, "Must be only digits")
    .nullable(true),
  taxID: Yup.string()
    .matches(REGULAR_EXPRESSION.NUMBER, "Must be only digits")
    .nullable(true),
};
/* =====================================================
   VENDOR SCHEMAS
===================================================== */
/*istanbul ignore next */
const VendorsSchema = {
  name: Yup.string().required("").nullable(true),
  email: Yup.string()
    .max(50, "Cannot be greater than 50 characters")
    .email("Invalid email")
    .required(""),
  secondaryEmail: Yup.string()
    .max(50, "Cannot be greater than 50 characters")
    .email("Invalid email")
    .nullable(true),
  phoneNumber: phoneWithExtensionSchema,
  zipCode: Yup.string().when("countryId", (countryId, schema) =>
    countryId === COUNTRY_OPTIONS.CANADA
      ? schema
          .matches(
            REGULAR_EXPRESSION.ZIP_CODE_CANADA,
            "Invalid Postal Code. Format is ANA NAN, where 'A' is an uppercase alphabet and 'N' is a digit.",
          )
          .nullable(true)
      : schema
          .matches(
            REGULAR_EXPRESSION.ZIP_CODE_USA,
            "Invalid Zip Code. Please enter a 5-digit Zip Code or a 9-digit ZIP+4 code in the format 12345 or 12345-6789.",
          )
          .nullable(true),
  ),
};
/*istanbul ignore next */
const VendorsContactSchema = {
  firstName: CommonSchema.firstName,
  lastName: CommonSchema.lastName,
  email: Yup.string()
    .max(50, "Cannot be greater than 50 characters")
    .email("Invalid email")
    .required(""),
  phoneNumber: phoneWithExtensionSchema,
  mobile: Yup.string()
    .matches(REGULAR_EXPRESSION.PHONE_NUMBER, "Incorrect format")
    .required(""),
};

const VendorsSolutionSchema = {
  name: Yup.string().required("").nullable(true),
  description: Yup.string().required("").nullable(true),
};
/* =====================================================
   VENDOR INTEGRATION SERVICE SCHEMA
===================================================== */
/*istanbul ignore next */
const VendorsSolutionServiceSchema = {
  serviceName: Yup.string().required("Service Name is required").nullable(true),
  serviceAPIURL: Yup.string()
    .required("Service API URL is required")
    .nullable(true),
  integrationMethod: Yup.number()
    .oneOf([1, 2, 3], "Invalid Integration Method selected")
    .typeError("Integration Method is required")
    .required("Integration Method is required")
    .nullable(true),
  integrationType: Yup.number().nullable(true),
  integrationTypeBasic: Yup.object().nullable(true),
  integrationTypeJWT: Yup.object().nullable(true),
  integrationTypeAPIKeyList: Yup.array().nullable(true),
  /* ---------- RESTORED & FIXED ---------- */

  integrationServiceRequest: Yup.lazy((value) => {
    if (
      !value ||
      (typeof value === "object" &&
        !Array.isArray(value) &&
        Object.keys(value).length === 0)
    ) {
      return Yup.mixed().nullable(true);
    }
    return Yup.object()
      .nullable(true)
      .shape({
        httpVerb: Yup.number().positive("").typeError("").required(""),
        requestGcpPath: Yup.string().when("httpVerb", (httpVerb, schema) =>
          httpVerb === 1
            ? schema.nullable(true)
            : schema.required("File is required"),
        ),
        requestParameterList: Yup.array().of(
          Yup.object().shape({
            parameterName: Yup.string().required(""),
            source: Yup.number().positive("").typeError("").required(""),
            eLabel: Yup.number()
              .nullable(true)
              .when("source", (source, schema) =>
                source === 1
                  ? schema
                      .positive("Base Value must be greater than 0")
                      .typeError("Base Value must be greater than 0")
                      .required("eLabel is required")
                  : schema.nullable(true),
              ),
            clientDetails: Yup.number()
              .nullable(true)
              .when("source", (source, schema) =>
                source === 2
                  ? schema
                      .positive("Base Value must be greater than 0")
                      .typeError("Base Value must be greater than 0")
                      .required("Client Folder Details are required")
                  : schema.nullable(true),
              ),
          }),
        ),
      });
  }),
  integrationServiceResponse: Yup.lazy((value) => {
    if (
      !value ||
      (typeof value === "object" &&
        !Array.isArray(value) &&
        Object.keys(value).length === 0)
    ) {
      return Yup.mixed().nullable(true);
    }
    return Yup.object()
      .nullable(true)
      .shape({
        responseGcpPath: Yup.string().required("File is required"),
        responseParameterList: Yup.array().of(
          Yup.object().shape({
            parameterName: Yup.string().required(""),
            source: Yup.number()
              .positive("")
              .typeError("")
              .required("Source is required"),
            eLabel: Yup.number()
              .nullable(true)
              .when("source", (source, schema) =>
                source === 1
                  ? schema
                      .positive("Base Value must be greater than 0")
                      .typeError("Base Value must be greater than 0")
                      .required("eLabel is required")
                  : schema.nullable(true),
              ),
            clientDetails: Yup.number()
              .nullable(true)
              .when("source", (source, schema) =>
                source === 2
                  ? schema
                      .positive("Base Value must be greater than 0")
                      .typeError("Base Value must be greater than 0")
                      .required("Client Folder Details are required")
                  : schema.nullable(true),
              ),
          }),
        ),
      });
  }),
};
/* =====================================================
   REUSABLE LINK PERSONAL INPUT SCHEMA
===================================================== */

const ReusableLinkPersonalInputSchema = {
  firstName: Yup.string().when("folderType", (folderType, schema) =>
    folderType === 1
      ? schema.required().min(1, "Too Short!").max(15)
      : schema.nullable(true).max(15),
  ),
  lastName: Yup.string().when("folderType", (folderType, schema) =>
    folderType === 1
      ? schema.required().min(1, "Too Short!").max(15)
      : schema.nullable(true).max(15),
  ),
  businessName: Yup.string().when("folderType", (folderType, schema) =>
    folderType === 2
      ? schema
          .required()
          .min(1, "Too Short!")
          .max(50, "Cannot be greater than 50 characters")
          .matches(/^[\s\S]*$/, "Invalid Business Name")
      : schema.nullable(true).max(50),
  ),
  streetAddress1: Yup.string().required("").max(100),
  streetAddress2: Yup.string().max(100),
  city: Yup.string().required("").min(2).max(100),
  email: Yup.string().max(50).email("Invalid email").required(""),
  phone: Yup.string()
    .matches(REGULAR_EXPRESSION.PHONE_NUMBER, "Incorrect format.")
    .required(""),
  mobile: Yup.string().matches(
    REGULAR_EXPRESSION.PHONE_NUMBER,
    "Incorrect format",
  ),
  zipCode: Yup.string().when("countryId", (countryId, schema) =>
    countryId === COUNTRY_OPTIONS.CANADA
      ? schema.matches(REGULAR_EXPRESSION.ZIP_CODE_CANADA).nullable(true)
      : schema.matches(REGULAR_EXPRESSION.ZIP_CODE_USA).nullable(true),
  ),
};
/* =====================================================
   ASSOCIATE + SUBSCRIPTION DOMAIN SCHEMAS
===================================================== */

const associateSchema = Yup.object().shape({
  distributorUserId: Yup.object().shape({
    value: Yup.number().moreThan(0, "Associated distributor is required"),
  }),
  associationId: Yup.string()
    .matches(REGULAR_EXPRESSION.ALPHANUMERIC, "Must be alpha numberic")
    .required("Association Id is required"),
  frequencyType: Yup.string()
    .equals(["1", "2", "3", "4"], "Select Frequency")
    .required("Select Frequency"),
  distributorSubscriptionId: Yup.number()
    .typeError("Invalid Subscription ID")
    .required("")
    .moreThan(0, ""),
});
export const distributorsubscriptionPlanDomainsSchema = {
  baseValue: Yup.number().min(0, "").typeError("").required(""),
  frequency: Yup.number().positive("").typeError("").required(""),
  effectiveFromDate: Yup.date().when(
    "distributorSubscriptionPlanId",
    (id, schema) =>
      id > 0
        ? schema.nullable().default(null).required("").typeError()
        : schema
            .nullable()
            .default(null)
            .required("")
            .min(moment(new Date().setHours(0, 0, 0, 0)))
            .typeError(),
  ),
  effectiveToDate: Yup.date().when(
    [
      "distributorSubscriptionPlanId",
      "savedEffectiveToDate",
      "effectiveFromDate",
    ],
    (id, saved, from, schema) => {
      if (!(id > 0) && !saved) {
        return from
          ? schema.min(Yup.ref("effectiveFromDate")).typeError()
          : schema.min(moment(new Date().setHours(0, 0, 0, 0))).typeError();
      }
      return schema.nullable().default(null).typeError();
    },
  ),
};

const distributorSubscriptionDomainsSchema = Yup.object().shape({
  name: Yup.string().required(""),
  subscriptionPlan: Yup.array().of(
    Yup.object().shape(distributorsubscriptionPlanDomainsSchema),
  ),
});

/* =====================================================
   FINAL EXPORTED SCHEMAS
===================================================== */
export const SignupSchema = Yup.object().shape({ ...CommonSchema });

export const UserSignupSchema = Yup.object().shape({
  ...CommonSchema,
  roles: Yup.array().min(1, "Select Roles").required(""),
});

export const DistributorSchema = Yup.object().shape({
  ...CommonSchema,
  distributorSubscriptionDomains: Yup.array().of(
    distributorSubscriptionDomainsSchema,
  ),
  roles: Yup.array().min(1, " ").required(""),
  brandName: Yup.string().nullable().required(""),
});

export const DistributorBasicInformationSchema = Yup.object().shape({
  ...CommonSchema,
  brandName: Yup.string().nullable().required(""),
  roles: Yup.array().min(1, "Select Roles").required(""),
});

export const EnterpriseGroupUserSchema = Yup.object().shape({
  ...CommonSchema,
  associateDistributors: Yup.array().of(associateSchema),
  roles: Yup.array().min(1, "Select Roles").required(""),
  brandName: Yup.string().nullable().required(""),
});

export const EnterpriseGroupBasicInformationSchema = Yup.object().shape({
  ...CommonSchema,
  roles: Yup.array().min(1, "Select Roles").required(""),
  brandName: Yup.string().nullable().required(""),
});

export const InternalUserSchema = Yup.object().shape({
  ...CommonSchema,
  roles: Yup.array().min(1, "Select Roles").required(""),
});

export const EmailSchema = Yup.object().shape({
  primaryEmail: Yup.string()
    .max(50, "Cannot be greater than 50 characters")
    .email("Invalid email")
    .required(""),
});

export const SubscriberSchema = Yup.object().shape({
  ...CommonSchema,
  subscription: Yup.object().shape({
    startDate: Yup.date().typeError("Invalid Date"),
  }),
  shippingZipCode: Yup.string().when(
    "shippingCountryId",
    (shippingCountryId, schema) =>
      shippingCountryId === COUNTRY_OPTIONS.CANADA
        ? schema
            .matches(
              REGULAR_EXPRESSION.ZIP_CODE_CANADA,
              "Invalid Postal Code. Format is ANA NAN, where 'A' is an uppercase alphabet and 'N' is a digit.",
            )
            .nullable(true)
        : schema
            .matches(
              REGULAR_EXPRESSION.ZIP_CODE_USA,
              "Invalid Zip Code. Please enter a 5-digit Zip Code or a 9-digit ZIP+4 code in the format 12345 or 12345-6789.",
            )
            .nullable(true),
  ),
  shippingPhoneNumber: Yup.string()
    .matches(REGULAR_EXPRESSION.PHONE_NUMBER, "Incorrect format")
    .nullable(true),
});

export const SubscriberBasicInformationSchema = Yup.object().shape({
  ...CommonSchema,
});

export const SettingsSchema = Yup.object().shape({
  writeToUs: Yup.string().email("Invalid email").required(""),
  aboutOrLegal: Yup.string()
    .matches(REGULAR_EXPRESSION.URL, "Must be a valid url")
    .required(""),
  helpOrSupport: Yup.string()
    .matches(REGULAR_EXPRESSION.URL, "Must be a valid url")
    .required(""),
});

export const VendorSchema = Yup.object().shape({ ...VendorsSchema });
export const VendorContactSchema = Yup.object().shape({
  ...VendorsContactSchema,
});
export const VendorIntegrationSolutionSchema = Yup.object().shape({
  ...VendorsSolutionSchema,
  solutionType: Yup.number()
    .typeError("Solution Type is required")
    .moreThan(0, "Solution Type is required")
    .required("Solution Type is required"),
  integrationType: Yup.number()
    .nullable(true)
    .when("solutionType", (solutionType, schema) =>
      solutionType === 1
        ? schema
            .oneOf([1, 2, 3], "Integration Type is required")
            .required("Integration Type is required")
        : schema.nullable(true),
    ),
  inchantedAddinType: Yup.number().when(
    "solutionType",
    (solutionType, schema) =>
      solutionType === 2
        ? schema
            .typeError("Inchanted Add-in is required")
            .moreThan(0, "Inchanted Add-in is required")
            .required("Inchanted Add-in is required")
        : schema.nullable(true),
  ),
  integrationTypeAPIKeyList: Yup.array().when(
    ["solutionType", "integrationType"],
    (solutionType, integrationType, schema) =>
      solutionType === 1 && integrationType === INTEGRATION_TYPE.API_KEY
        ? schema
            .of(
              Yup.object().shape({
                keyName: Yup.string().required("Key Name is required"),
                keyValue: Yup.string().when(
                  "hasKeyValue",
                  (hasKeyValue, schema) =>
                    hasKeyValue
                      ? schema.required("Key Value is required")
                      : schema.nullable(true),
                ),
                addTo: Yup.number()
                  .required("Add To is required")
                  .typeError("Add To is required")
                  .moreThan(0, "Please select a valid option"),
                hasKeyValue: Yup.boolean().nullable(true),
              }),
            )
            .min(1, "At least one API Key is required")
        : schema.nullable(true),
  ),
});
export const VendorIntegrationServiceSchema = Yup.object().shape({
  ...VendorsSolutionServiceSchema,
});
export const ReusableLinkSchema = Yup.object().shape({
  ...ReusableLinkPersonalInputSchema,
});
